import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/cable';

export default class extends Controller {
  static targets = [
    'step',
    'progress',
    'progressBar',
    'backButton',
    'title',
    'close',
    'continueButton',
    'profileForm',
    'amountForm',
    'amountInput',
    'submitError',
    'parcours',
    'result',
    'resultProcessing',
    'resultSuccess',
    'resultFailure',
    'resultError',
  ];
  static values = {
    currentStep: { type: Number, default: 0 },
    initialStep: String,
    currentProfileId: String,
    genericError: String,
  };

  connect() {
    const index = this.stepTargets.findIndex((step) => step.dataset.stepName === this.initialStepValue);

    if (index > 0) this.currentStepValue = index;
  }

  disconnect() {
    this.subscription?.unsubscribe();
  }

  next() {
    if (this.#requiresProfileSwitch) {
      this.profileFormTarget.requestSubmit();
      return;
    }

    if (this.#onAmountStep) {
      if (this.amountFormTarget.reportValidity()) this.#submitReservation();
      return;
    }

    if (this.currentStepValue < this.#lastStepIndex) {
      this.currentStepValue += 1;
    }
  }

  previous() {
    if (this.currentStepValue > 0) {
      this.currentStepValue -= 1;
    }
  }

  clearSubmitError() {
    if (this.hasSubmitErrorTarget) this.submitErrorTarget.classList.add('d-none');
  }

  async #submitReservation() {
    this.continueButtonTarget.disabled = true;
    const form = this.amountFormTarget;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json();

      if (response.ok) {
        this.#revealResult(data.id);
        return;
      }

      this.#failSubmission(data.error);
    } catch {
      this.#failSubmission();
    }
  }

  #failSubmission(message) {
    this.continueButtonTarget.disabled = false;
    this.#showSubmitError(message || this.genericErrorValue);
  }

  #showSubmitError(message) {
    if (!this.hasSubmitErrorTarget || !message) return;

    this.submitErrorTarget.textContent = message;
    this.submitErrorTarget.classList.remove('d-none');
  }

  #revealResult(intentionId) {
    this.parcoursTarget.classList.add('d-none');
    this.resultTarget.classList.remove('d-none');
    this.#subscribeToIntention(intentionId);
  }

  #subscribeToIntention(intentionId) {
    this.subscription = consumer.subscriptions.create(
      { channel: 'SubscriptionIntentionChannel', subscription_intention_id: intentionId },
      {
        received: (data) => this.#showResultOutcome(data.status),
        rejected: () => this.#showResultOutcome('error'),
      },
    );
  }

  #showResultOutcome(status) {
    const outcome = status === 'success' || status === 'failure' ? status : 'error';

    this.resultProcessingTarget.classList.add('d-none');
    this.resultSuccessTarget.classList.toggle('d-none', outcome !== 'success');
    this.resultFailureTarget.classList.toggle('d-none', outcome !== 'failure');
    this.resultErrorTarget.classList.toggle('d-none', outcome !== 'error');
  }

  currentStepValueChanged() {
    if (!this.hasStepTarget) return;

    this.#showCurrentStep();
    this.#updateTitle();
    this.#updateContinueButton();
    this.#updateProgress();
    this.#updateBackButton();
    this.#updateCloseButton();
  }

  #showCurrentStep() {
    this.stepTargets.forEach((step, index) => {
      step.classList.toggle('d-none', index !== this.currentStepValue);
    });
  }

  #updateTitle() {
    if (!this.hasTitleTarget) return;

    const title = this.stepTargets[this.currentStepValue]?.dataset.stepTitle;

    if (title) this.titleTarget.textContent = title;
  }

  #updateContinueButton() {
    if (!this.hasContinueButtonTarget) return;

    const label = this.stepTargets[this.currentStepValue]?.dataset.stepContinue;

    if (label) this.continueButtonTarget.textContent = label;
  }

  #updateProgress() {
    if (!this.hasProgressTarget) return;

    const onWelcomeStep = this.currentStepValue === 0;
    this.progressTarget.classList.toggle('d-none', onWelcomeStep);

    if (onWelcomeStep) return;

    const percentage = Math.round((this.currentStepValue / this.#lastStepIndex) * 100);
    this.progressBarTarget.style.width = `${percentage}%`;
    this.progressTarget.setAttribute('aria-valuenow', percentage);
  }

  #updateBackButton() {
    if (!this.hasBackButtonTarget) return;

    this.backButtonTarget.classList.toggle('d-none', this.currentStepValue === 0);
  }

  #updateCloseButton() {
    if (!this.hasCloseTarget) return;

    const showsClose = this.stepTargets[this.currentStepValue]?.dataset.stepClose === 'true';
    this.closeTarget.classList.toggle('d-none', !showsClose);
  }

  get #requiresProfileSwitch() {
    if (!this.hasProfileFormTarget) return false;

    const profileStepIndex = this.stepTargets.findIndex((step) => step.contains(this.profileFormTarget));
    if (this.currentStepValue !== profileStepIndex) return false;

    const selected = this.profileFormTarget.querySelector('input[name="profile_id"]:checked');

    return selected !== null && selected.value !== this.currentProfileIdValue;
  }

  get #onAmountStep() {
    if (!this.hasAmountFormTarget) return false;

    const amountStepIndex = this.stepTargets.findIndex((step) => step.contains(this.amountFormTarget));

    return this.currentStepValue === amountStepIndex;
  }

  get #lastStepIndex() {
    return this.stepTargets.length - 1;
  }
}
